import { Turbo } from '@hotwired/turbo-rails'

/**
 * Visite qui remplace la page EN PLACE dans l'app native : même écran, autre
 * URL. Deux usages, chacun par UN SEUL chemin :
 * - le changement de sous-onglet du profil, quel que soit ce qui le déclenche —
 *   le tap sur l'onglet, ou le geste de la barre d'onglets natif
 *   (`bridge--tab-reselect`, marche `reset`) ;
 * - le tri d'une liste, depuis le sous-menu « Trier par » du ⋯ natif
 *   (`bridge--menu`, entrées `visit: 'replace'`).
 *
 * `startVisit` et pas `Turbo.visit` : `Turbo.visit` PROPOSE la visite, et le
 * natif la route alors comme une navigation d'écran — pile remplacée, capture
 * de l'écran sortant, nouveau fragment. On y perd l'animation de dépliage et
 * on gagne un écran blanc de trois quarts de seconde. `startVisit` reste dans
 * la page : le natif ne voit rien passer, le panneau glisse, rien ne clignote.
 * Il suit quand même l'URL affichée, pour y revenir intacte au retour d'un
 * écran poussé depuis là (`WebFragment.location` côté Android).
 *
 * Le panneau est DONNÉ par l'appelant (cible `panel` du contrôleur) : le module
 * ne connaît aucun id. Une page sans panneau passe `null` — la visite se fait,
 * le cadre du slide ne se pose pas.
 *
 * `keepScroll` garde la position de défilement d'une page à l'autre : c'est ce
 * que veut le tap, l'écran restant sous les yeux. La remise en place à la sortie
 * du profil, elle, se fait sur une page CACHÉE — y garder le défilement ferait
 * revenir l'utilisateur au milieu d'une liste qu'il n'a jamais parcourue.
 */
export function visitProfileTab(link, panel, options) {
  visitInPlace(link.href, panel, options)
}

export function visitInPlace(url, panel, { keepScroll = true } = {}) {
  // La position de défilement survit au changement d'onglet. AVANT la visite :
  // le cadre du panneau pendant le slide (animations.css, --panel-frame) va de
  // son haut au bas de l'écran tel qu'il est défilé, et la hauteur du document
  // est figée — sinon la page raccourcissait au clic et le navigateur recadrait
  // d'un coup. Puis Turbo, qui remet en haut après chaque rendu d'une visite
  // (aperçu du cache, puis réponse) tant que `scrolled` est faux : marquée dès
  // le départ, la visite n'y touche pas.
  if (panel) {
    const frame = Math.max(window.innerHeight - panel.getBoundingClientRect().top, 0)
    document.documentElement.style.setProperty('--panel-frame', `${frame}px`)
  }
  holdDocumentHeight()
  Turbo.navigator.startVisit(url, null, { action: 'replace' })
  const visit = Turbo.navigator.currentVisit
  if (visit && keepScroll) visit.scrolled = true
}

/**
 * Page entrante trop courte pour la position courante : sans rien faire, le
 * navigateur recadre d'un coup sec au rendu. On fige la hauteur du document
 * pendant la visite (min-height sur <html>), puis, une fois la page chargée,
 * on descend en douceur jusqu'à la position la plus basse possible avant de
 * rendre sa hauteur naturelle au document.
 */
let heldHeight = false

function holdDocumentHeight() {
  document.documentElement.style.minHeight = `${document.documentElement.scrollHeight}px`
  heldHeight = true
}

function releaseDocumentHeight() {
  document.documentElement.style.minHeight = ''
  document.documentElement.style.removeProperty('--panel-frame')
  heldHeight = false
}

document.addEventListener('turbo:load', () => {
  if (!heldHeight) return
  // Le body (min-h-dvh) mesure le contenu réel ; <html> porte la hauteur figée.
  const max = Math.max(0, document.body.scrollHeight - window.innerHeight)
  if (window.scrollY <= max) { releaseDocumentHeight(); return }

  window.scrollTo({ top: max, behavior: 'smooth' })
  let released = false
  const release = () => { if (!released) { released = true; releaseDocumentHeight() } }
  document.addEventListener('scrollend', release, { once: true })
  setTimeout(release, 800)
})
